import { useCoveo } from "components/coveo/Coveo"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useSuspender } from "contexts/Suspender"
import { useUser } from "contexts/User"
import { useMotionValue } from "framer-motion"
import React, { createContext, useContext, useMemo } from "react"

const AggregatorContext = createContext({})

export function getTerms(c, t) {
  return Array.from(
    new Set(
      c
        .reduce(
          (a, v) =>
            a.concat(
              v?.covers?.component?.[0]?._content_type_uid === "coveroller_dyna"
                ? [v.covers.component[0].taxonomies.filter(v => v.taxonomy_uid === t).map(v => v.term_uid)]
                : v?.miscs?.component?.[0]?._content_type_uid === "aggregator"
                  ? [v.miscs.component[0].taxonomies.filter(v => v.taxonomy_uid === t).map(v => v.term_uid)]
                  : v?.miscs?.component?.[0]?._content_type_uid === "roller_loop_dyna"
                    ? [v.miscs.component[0]?.taxonomies.filter(v => v.taxonomy_uid === t).map(v => v.term_uid)]
                    : []
            ),
          []
        )
        .flat()
    )
  )
}

export const AggregatorProvider = ({ compagg = [], compaggcat = [], children }) => {
  const console = useConsole()
  const coveo = useCoveo()
  const env = useEnv()
  const user = useUser()
  const locale = useLocale()
  const dyna_selection = useMotionValue("")
  const earthTextureCache = new Map()

  const compaggKey = compagg.join("|")
  const lang = locale?.current?.codes?.ws === "en" ? "en-GB" : locale?.current?.codes?.ws

  const [[err, results] = [], ssr] = useSuspender(
    ["aggregator", compaggKey, lang, user?.countryCode?.toLowerCase() || ""],
    () => {
      if (!compagg.length) {
        return [null, []]
      }
      return (async () => {
        try {
          let token = typeof window === "undefined" ? coveo?.pipelineSAP || env.pipelineSAP : null

          if (process.browser && coveo) {
            await coveo.sync()
            token = coveo.pipelineSAP
          }

          if (!token) throw new Error("No Coveo pipelineSAP token available")

          const url = env.endpoint

          const cat = compaggcat.length ? ` AND @category=(${compaggcat.join(" OR ")})` : ""

          const request = new Request(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              q: "",
              generateJson: true,
              cq: `@rlx_lang==${lang} AND @aggregator=(${compagg.join(" OR ")})${cat}`,
              fieldsToInclude: [
                "aggregator",
                "cs_uid",
                "title",
                "name",
                "baseline",
                "slug",
                "overlay",
                "tag",
                "public_id_image_0",
                "public_id_video_0",
                "public_image_0_width",
                "public_image_0_height",
                "public_id_image_1",
                "public_id_video_1",
                "public_image_1_width",
                "public_image_1_height",
                "public_id_image_2",
                "public_id_video_2",
                "public_image_2_width",
                "public_image_2_height",
                "documentType",
                "discipline",
                "category",
                "taxonomies",
                "classification",
                "link_href",
                "link_label",
                "link_aria_label",
                "parent_page",
                "parent_element_uid",
                "parent_slug",
                "label",
                "link",
                "id",
                "eventId",
                "lat",
                "lng",
                "ariaLabel",
                "surtitle",
                "video",
                "filter",
                "languages_taxonomy",
                "cover_asset_0_duration",
                "cover_asset_0_width",
                "cover_asset_0_height",
                "cover_asset_0_public_id",
                "cover_asset_0_secure_url",
                "cover_asset_1_duration",
                "cover_asset_1_width",
                "cover_asset_1_height",
                "cover_asset_1_public_id",
                "cover_asset_1_secure_url",
                "cover_asset_2_duration",
                "cover_asset_2_width",
                "cover_asset_2_height",
                "cover_asset_2_public_id",
                "cover_asset_2_secure_url",
                "cover_asset_3_duration",
                "cover_asset_3_width",
                "cover_asset_3_height",
                "cover_asset_3_public_id",
                "cover_asset_3_secure_url",
                "description",
                "start_date",
                "end_date",
              ],
              numberOfResults: 200,
              firstResult: 0,
              sortCriteria: "relevancy",
              enableDidYouMean: false,
              enableQuerySyntax: false,
              groupBy: [{ field: "@title", maximumNumberOfValues: 1 }],
              context: {
                country: user.countryCode.toLowerCase(),
                location: "coveroller",
              },
            }),
          })

          const response = await fetch(request)
          const responseText = await response.text()

          if (!response.ok) throw new Error(`Coveo Aggregator ${response.status}: ${responseText}`)
          //
          const body = JSON.parse(responseText)
          // const results = (body.results || []).filter(v => String(v?.raw?.parent_page ?? "").toLowerCase() !== "true")
          const results = body.results || []

          return [null, results]
        } catch (err) {
          console.error("Aggregator error:", err)
          return [err, null]
        }
      })()
    },
    true
  )

  const data = useMemo(() => [err, results], [err, results])

  return (
    <>
      {ssr}
      <AggregatorContext.Provider value={{ data, dyna_selection, earthTextureCache }}>{children}</AggregatorContext.Provider>
    </>
  )
}

export function useAggregator() {
  return useContext(AggregatorContext)
}
